import { PUBLIC_APP_URL } from '../config';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  Show,
  SimpleShowLayout,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  FilterButton,
  SearchInput,
  TopToolbar,
  ExportButton,
  EditButton,
  DeleteButton,
  useRecordContext,
  FunctionField,
  downloadCSV,
} from 'react-admin';
import { Box, Typography, Chip, IconButton, Tooltip, Avatar } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';

// RBAC
import { Resource, Action, useRBAC } from '../rbac';

/**
 * Get card type icon
 */
function getCardTypeIcon(cardType: string) {
  switch (cardType) {
    case 'business':
      return <StorefrontIcon />;
    case 'evenement':
      return <EventIcon />;
    default:
      return <CreditCardIcon />;
  }
}

/**
 * Get card type label
 */
function getCardTypeLabel(cardType: string) {
  switch (cardType) {
    case 'presentation':
      return 'Présentation';
    case 'professionnel':
      return 'Professionnel';
    case 'business':
      return 'Commerce';
    case 'evenement':
      return 'Événement';
    default:
      return cardType;
  }
}

/**
 * Get card type color
 */
function getCardTypeColor(cardType: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' {
  switch (cardType) {
    case 'presentation':
    case 'professionnel':
      return 'primary';
    case 'business':
      return 'success';
    case 'evenement':
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Card Display Field
 */
function CardDisplayField() {
  const record = useRecordContext();
  if (!record) return null;

  const primaryColor = record.theme?.primaryColor || '#C97B63';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        sx={{
          width: 40,
          height: 40,
          backgroundColor: primaryColor,
          color: 'white',
        }}
      >
        {getCardTypeIcon(record.cardType)}
      </Avatar>
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {record.displayName}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {record.title || record.company || '-'}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Card Type Chip
 */
function CardTypeChip() {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Chip
      icon={getCardTypeIcon(record.cardType)}
      label={getCardTypeLabel(record.cardType)}
      size="small"
      color={getCardTypeColor(record.cardType)}
      variant="outlined"
    />
  );
}

/**
 * Card Status Chip
 */
function CardStatusChip() {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Chip
      label={record.status === 'ACTIVE' ? 'Actif' : 'Archivé'}
      size="small"
      color={record.status === 'ACTIVE' ? 'success' : 'default'}
    />
  );
}

/**
 * Card Stats
 */
function CardStats() {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <VisibilityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="body2">{record.viewCount || 0}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ShareIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="body2">{record.shareCount || 0}</Typography>
      </Box>
    </Box>
  );
}

/**
 * Card Actions with RBAC
 */
function CardRowActions() {
  const record = useRecordContext();
  const { can } = useRBAC();

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {/* Edit button - only if user can edit cards */}
      {can(Resource.CARDS, Action.EDIT) && <EditButton />}

      {/* Delete button - only if user can delete cards */}
      {can(Resource.CARDS, Action.DELETE) && <DeleteButton />}

      {/* View public card - always visible */}
      <Tooltip title="Voir la carte">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            window.open(`${PUBLIC_APP_URL}/c/${record.slug}`, '_blank');
          }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * Cards Exporter with Location Data
 */
const cardsExporter = (records: any[]) => {
  // Extract data with location info
  const dataToExport = records.map((record) => {
    // Extract location data based on card type
    let locationData = {
      location_address: '',
      location_latitude: '',
      location_longitude: '',
    };

    // For Commerce cards (visite_guidee) - extract from businessData.location
    if (record.cardType === 'business' && record.businessData?.location) {
      const loc = record.businessData.location;
      locationData = {
        location_address: loc.address || '',
        location_latitude: loc.coordinates?.lat?.toString() || '',
        location_longitude: loc.coordinates?.lng?.toString() || '',
      };
    }
    // For Event cards (evenement) - extract from eventData.venue
    else if (record.cardType === 'evenement' && record.eventData?.venue) {
      const venue = record.eventData.venue;
      locationData = {
        location_address: venue.address || '',
        location_latitude: venue.coordinates?.lat?.toString() || '',
        location_longitude: venue.coordinates?.lng?.toString() || '',
      };
    }

    // Return essential fields + location
    return {
      id: record.id,
      cardType: record.cardType,
      displayName: record.displayName,
      title: record.title || '',
      company: record.company || '',
      slug: record.slug,
      viewCount: record.viewCount || 0,
      shareCount: record.shareCount || 0,
      status: record.status,
      createdAt: record.createdAt,
      ...locationData,
    };
  });

  // Manual CSV generation (browser-compatible)
  const headers = [
    'id',
    'cardType',
    'displayName',
    'title',
    'company',
    'slug',
    'viewCount',
    'shareCount',
    'status',
    'createdAt',
    'location_address',
    'location_latitude',
    'location_longitude',
  ];

  // Escape CSV values
  const escapeCSV = (value: any) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Generate CSV
  const csvRows = [
    headers.join(','),
    // `headers` est un string[] littéral (pas un tuple de clés exactes) —
    // `header` ne se rétrécit pas automatiquement en keyof row. Les valeurs
    // de `headers` correspondent bien aux clés de dataToExport (vérifié
    // ci-dessus), cast explicite plutôt qu'un any implicite.
    ...dataToExport.map((row) => headers.map((header) => escapeCSV(row[header as keyof typeof row])).join(',')),
  ];

  const csv = csvRows.join('\n');
  downloadCSV(csv, 'cards');
};

/**
 * Cards List Filters
 */
const cardFilters = [
  <SearchInput source="q" alwaysOn placeholder="Rechercher..." key="search" />,
  <SelectInput
    key="status"
    source="status"
    label="Statut"
    choices={[
      { id: 'ACTIVE', name: 'Actif' },
      { id: 'ARCHIVED', name: 'Archivé' },
    ]}
  />,
  <SelectInput
    key="cardType"
    source="cardType"
    label="Type"
    choices={[
      { id: 'presentation', name: 'Présentation' },
      { id: 'professionnel', name: 'Professionnel' },
      { id: 'business', name: 'Commerce' },
      { id: 'evenement', name: 'Événement' },
    ]}
  />,
];

/**
 * Cards List Actions with RBAC
 */
function CardListActions() {
  const { can } = useRBAC();

  return (
    <TopToolbar>
      <FilterButton />
      {/* Export button - only if user can export */}
      {can(Resource.CARDS, Action.EXPORT) && <ExportButton />}
    </TopToolbar>
  );
}

/**
 * Cards List
 */
export function CardList() {
  return (
    <List
      filters={cardFilters}
      actions={<CardListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      exporter={cardsExporter}
    >
      <Datagrid
        rowClick="show"
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-row': {
            cursor: 'pointer',
          },
        }}
      >
        <FunctionField label="Carte" render={() => <CardDisplayField />} />
        <FunctionField label="Type" render={() => <CardTypeChip />} />
        <FunctionField label="Statut" render={() => <CardStatusChip />} />
        <FunctionField label="Stats" render={() => <CardStats />} />
        <DateField source="createdAt" label="Création" locales="fr-FR" />
        <FunctionField label="Actions" render={() => <CardRowActions />} />
      </Datagrid>
    </List>
  );
}

/**
 * Card Show with RBAC actions
 */
export function CardShow() {
  const { can } = useRBAC();

  return (
    <Show
      actions={
        <TopToolbar>
          {can(Resource.CARDS, Action.EDIT) && <EditButton />}
          {can(Resource.CARDS, Action.DELETE) && <DeleteButton />}
        </TopToolbar>
      }
    >
      <SimpleShowLayout>
        <FunctionField label="Carte" render={() => <CardDisplayField />} />
        <TextField source="slug" label="Slug" />
        <FunctionField label="Type" render={() => <CardTypeChip />} />
        <FunctionField label="Statut" render={() => <CardStatusChip />} />
        <TextField source="displayName" label="Nom d'affichage" />
        <TextField source="title" label="Titre" emptyText="-" />
        <TextField source="company" label="Entreprise" emptyText="-" />
        <TextField source="bio" label="Bio" emptyText="-" />
        <TextField source="location" label="Localisation" emptyText="-" />
        <TextField source="businessCategory" label="Catégorie" emptyText="-" />
        <NumberField source="viewCount" label="Vues" />
        <NumberField source="shareCount" label="Partages" />
        <DateField source="createdAt" label="Date de création" showTime locales="fr-FR" />
        <DateField source="updatedAt" label="Dernière mise à jour" showTime locales="fr-FR" />
      </SimpleShowLayout>
    </Show>
  );
}

/**
 * Card Edit (protected by resource permission in App.tsx)
 */
export function CardEdit() {
  return (
    <Edit>
      <SimpleForm>
        <TextInput source="displayName" label="Nom d'affichage" fullWidth />
        <TextInput source="title" label="Titre" fullWidth />
        <TextInput source="company" label="Entreprise" fullWidth />
        <TextInput source="bio" label="Bio" multiline rows={4} fullWidth />
        <SelectInput
          source="status"
          label="Statut"
          choices={[
            { id: 'ACTIVE', name: 'Actif' },
            { id: 'ARCHIVED', name: 'Archivé' },
          ]}
        />
      </SimpleForm>
    </Edit>
  );
}
